import { router } from '@inertiajs/vue3'
import { debounce, isEmpty } from 'lodash-es'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { DETAIL_ESG } from '../constants'
import { deepCopy } from '../services/utilities'
import { useEsgStore } from './useEsgStore'
import { useInfraStore } from './useInfraStore'
import { useLandBoardStore } from './useLandBoardStore'
import { useRealEstateStore } from './useRealEstateStore'
import { useTransactionComparisonMasterDetails } from '@/composables/useTransactionComparisonMasterDetails'

export const useDetailStore = defineStore('detail', () => {
  const objectId = ref(null)
  const cadasterId = ref(null)
  const ehrId = ref(null)

  const restrictions = ref([])

  // Üldplaneering info for the Planeeringud card (RED-293).
  const planningInfo = shallowRef(null)
  const planningInfoError = shallowRef(false)
  // Current plannings-map viewport ({ bbox: [minE,minN,maxE,maxN] in
  // EPSG:3301, width, height }) — captured on every map export so the docx
  // legend can be scoped to the categories actually in view.
  const planningMapView = shallowRef(null)
  // True while GET /planning/{id} is in flight: until it answers nobody knows
  // whether an overlay is coming, and `planningInfo === null` cannot tell
  // "not fetched" from "pending".
  const planningInfoPending = shallowRef(false)

  const fetchPlanningInfo = async id => {
    if (!id) {
      return
    }
    planningInfo.value = null
    planningInfoError.value = false
    planningInfoPending.value = true
    try {
      const { data } = await axios.get('/planning/' + id)
      // A payload always ends in an applyOverlay() on the card (a tier, or
      // none). Invalidate the capture HERE, in the same synchronous step
      // that clears `pending`: the card's watcher bumps again on its own
      // flush tick, and an export waiting on planningMapReady would slip
      // through the gap in between (seen in e2e: the POST left 130 ms
      // before the overlay tiles were requested).
      planningMapGeneration.value += 1
      planningInfo.value = data
    } catch {
      planningInfoError.value = true
    } finally {
      planningInfoPending.value = false
    }
  }

  // Capture generations for the plannings map (RED-293). The docx
  // ${kaart-planeeringud} is the browser capture of the card map, and the
  // maakasutus overlay is switched on only after /planning/{id} answers —
  // seconds after the map's first capture. The generation is bumped before
  // every overlay change (payload landed, applyOverlay, heal) and the card
  // records planningCaptureGeneration with the capture that followed it,
  // so the export can tell a capture of the current overlay from a stale
  // one.
  const planningMapGeneration = shallowRef(0)
  const planningCaptureGeneration = shallowRef(0)
  const planningMapReady = computed(
    () =>
      !planningInfoPending.value &&
      planningCaptureGeneration.value >= planningMapGeneration.value,
  )

  // Recapture signal for the plannings card, the same contract as
  // esgRecaptureRequest below.
  const planningRecaptureRequest = shallowRef(0)
  const requestPlanningRecapture = () => {
    planningRecaptureRequest.value += 1
  }

  // The compact legend for the plannings-map viewport (EPSG:3301 bbox), the
  // same image the report bakes into its map (RED-293 round 3). Null when
  // nothing is drawn there or the request failed.
  const fetchPlanningLegend = async ({ tier, planid, bbox, scale }) => {
    if (!tier || !Array.isArray(bbox) || bbox.length !== 4) {
      return null
    }
    try {
      const { data } = await axios.get('/planning/legend', {
        params: {
          tier,
          planid: planid ?? undefined,
          bbox: bbox.map(v => Math.round(v * 100) / 100).join(','),
          scale: scale ?? undefined,
        },
      })
      return data?.legend ?? null
    } catch {
      return null
    }
  }

  // The overlay source for a land-use tier; `verify` makes the backend probe
  // the cached XGIS2 service id and heal it when dead (RED-293). Resolves to
  // null on failure — the caller keeps whatever it has.
  const fetchOverlaySource = async (tier, verify = false) => {
    if (!tier) {
      return null
    }
    try {
      const { data } = await axios.get('/planning/overlay-source', {
        params: { tier, verify: verify ? 1 : 0 },
      })
      return data?.overlay ?? null
    } catch {
      return null
    }
  }

  const documents = ref([])
  const selectedDocuments = ref([])

  const landBoardStore = useLandBoardStore()
  const { landBoard } = storeToRefs(landBoardStore)
  const { setLandBoardData } = landBoardStore

  const infraStore = useInfraStore()
  const { infra } = storeToRefs(infraStore)

  const isExporting = ref(false)

  const buildings = ref([])
  const selectedBuildings = ref([])

  const evaluationId = ref(null)
  const evaluationAuthor = ref(null)
  const evaluationApproverId = ref(null)
  const evaluationLivingRoom = ref(null)
  const evaluationNotLivingRoom = ref(null)
  const evaluationTemplateId = ref(null)

  const interior = ref({})

  const getSelectedDocuments = computed(() => {
    return selectedDocuments.value || []
  })

  const getInitialMainForm = () => {
    return {
      eksperthinnangu_nr: '',
      aadress: '',
      registriosa_nr: '',
      katastritunnus: '',
      tellija: '',
      ylevaatuse_juures: '',
      tellimusleping: '',
      omanik: '',
      omandivorm: '',
      eriomandi_pind: '',
      erikasutus: '',
      notariaalne_kaasomandi_kokkulepe: '',
      mottlise_osa_suurus: '',
      eeldused: '',
      yyrilepingud: '',
      aruande_kuupaev: '',
      ylevaatuse_kuupaev: '',
      vaartuse_kuupaev: '',
      vara: '',
      yldine_seisukord: '',
      hindamise_eesmark: '',
      kaibemaks: '',
      likviidsus: '',
      hindamise_tapsus: '',
      ylevaatuse_hindaja: '',
      ylevaatuse_ulatus: '',
      ylevaatuse_pohjalikkus: '',
    }
  }

  const mainForm = ref(getInitialMainForm())

  const ehrGeneral = ref({
    'ehitise-liik': '',
    'ehitise-nimetus': '',
    ehrkood: '',
    'kasutuselevotu-aasta': '',
    'kavandatav-kasutuselevotu-kuupev': '',
    'ehitise-seisund': '',
    'peamine-kasutusotstarve': '',
    'ehitise-koha-aadress': '',
    'korterite-arv': '',
    energiaklass: '',
    'energiaklass-kuupaev': '',
    'energiaklass-tohususarv': '',
    'energiaklass-kuupaev-lopp': '',
    'energiaklass-dokumendi-nr': '',
    'energiaklass-dokumendi-liik': '',
    'energiaklass-tarnitud-energia': '',
  })

  const ehrTechnical = ref({
    'ehitisealune-pind': '',
    'maapealse-osa-alune-pind': '',
    'maapealseid-korruseid': '',
    'maa-alused-korrused': '',
    lift: '',
    snp: '',
    'koetav-pind': '',
    'eluruumide-pind': '',
    'mitteeluruumide-pind': '',
    'hoone-maht': '',
    'maapeal-osa-maht': '',
    'yldkasutatav-pind': '',
    tehnopind: '',
    'pindalade-vastavus': '',
    markused: '',
  })

  const ehrConstruction = ref({
    vundamendi_liik: '',
    konstruktsioonide_materjal: '',
    valisseina_valisviimistlus: '',
    valisseina_liik: '',
    katuste_katuselagede_materjal: '',
    vahelagede_materjal: '',
    katusekate: '',
    valisuksed: '',
    siseuksed: '',
    aknad: '',
    rodu: [],
    siseseinad: '',
    vastavus_ehr: '',
    hoone_seisuskord: '',
    hoone_markused: '',
  })

  const ehrCommunal = ref({
    elektrisysteemi_liik: '',
    veevarustuse_liik: '',
    kanalisatsiooni_liik: '',
    soojusvarustuse_liik: '',
    soojusallikas: '',
    energiaallikas: '',
    ventilatsiooni_liik: '',
    jahutussysteemi_liik: '',
    gaasi_olemasolu: '',
    side: '',
    tehnovarustus_seisuskord: '',
    tehnovarustus_markused: '',
  })

  const geometry = shallowRef(null)

  const ehrLivingRoom = ref([])
  const ehrNotLivingRoom = ref([])
  const ehrGeometry = computed({
    get() {
      if (geometry.value) {
        return geometry.value
      }
      if (landBoard.value?.geometry) {
        return [landBoard.value.geometry]
      }
      return null
    },
    set(value) {
      geometry.value = value
    },
  })
  const ehrAds = ref({})
  const ehrLoading = ref(false)
  const ehrLoaded = ref(false)

  const {
    realEstateGeneral,
    realEstatePart1Main,
    realEstatePart1Entries,
    realEstatePart2,
    realEstatePart3,
    realEstatePart4,
  } = storeToRefs(useRealEstateStore())

  const esgStore = useEsgStore()
  const { fetchAllEsgData } = esgStore
  const { esgData, esgSummary } = storeToRefs(esgStore)
  const { storeMasterData } = useTransactionComparisonMasterDetails()

  const mapExport = ref({
    micro: '',
    macro: '',
    restriction: '',
    landBoard: '',
    noise_level: '',
    traffic_volume: '',
    mineral_deposit: '',
    hazardous_installations: '',
    residual_pollution: '',
    radon_risk: '',
    flood_risk: '',
    heat_islands: '',
    infra: '',
    plannings: '',
  })

  const setPropertyData = async (cadaster, data) => {
    // Seed the cadaster independent of EHR data — a land parcel (RED-265) has
    // no xRoadEhr, so setEhrData never runs and every cadaster-driven section
    // (restrictions, plannings, kinnistusraamat) would otherwise stay dead.
    const resolvedCadaster = cadaster ?? data?.landBoard?.katastritunnus ?? null
    if (resolvedCadaster && !cadasterId.value) {
      cadasterId.value = resolvedCadaster
      // Do not overwrite if user made changes
      if (mainForm.value['katastritunnus'] === '') {
        mainForm.value['katastritunnus'] = resolvedCadaster
      }
    }

    // Same non-overwriting seed for the address — buildings get it from
    // ehrAds.taisaadress in setEhrData, which never runs for a land parcel.
    if (mainForm.value['aadress'] === '' && data?.landBoard?.taisaadress) {
      mainForm.value['aadress'] = data.landBoard.taisaadress
    }

    if (data?.xRoadEhr?.general) {
      setEhrData(cadaster, data.xRoadEhr)
    }
    if (data?.xRoadEhr && !data.landBoard) {
      storeMasterData()
    }

    if (data?.landBoard) {
      setLandBoardData(data.landBoard)

      if (data.landBoard?.bbox) {
        // Building footprint, when available, is the better spatial reference
        // for polygon-intersect ESG layers — the export then matches what the
        // map shows at the building's pixels, not at the parcel centroid.
        const buildingGeometry = data?.xRoadEhr?.geometry?.[0] ?? null
        await fetchAllEsgData(data.landBoard.bbox, buildingGeometry)
      }
    }

    // RED-355: the annex C form is there even when no parcel outline lets the
    // ESG lookups run — every map factor then reads „info puudub“ and the
    // valuer can still answer and save the rest. Only when nothing built it
    // yet: a QV re-sync that skips the fetch keeps the rows it has.
    if (esgStore.annexCEnabled && !data?.landBoard?.bbox && !esgSummary.value.length) {
      esgStore.initializeEsgSummary()
    }
  }

  const setEhrData = (cadaster, data) => {
    if (!data?.general) {
      ehrLoading.value = false
      return
    }

    ehrId.value = data.general.ehrkood
    cadasterId.value = cadaster ?? data.cadaster?.[0] ?? ''
    ehrAds.value = data.ehitiseAndmed
    ehrGeneral.value = data.general
    esgStore.fetchKorteruhistu(ehrGeneral.value['ehitise-koha-aadress'])

    if (data.technical) {
      Object.entries(data.technical).forEach(([key, value]) => {
        if (!isEmpty(value) && isEmpty(ehrTechnical.value[key]) && Object.hasOwn(ehrTechnical.value, key)) {
          ehrTechnical.value[key] = value
        }
      })
    }

    if (data.construction) {
      Object.entries(data.construction).forEach(([key, value]) => {
        if (!isEmpty(value) && isEmpty(ehrConstruction.value[key]) && Object.hasOwn(ehrConstruction.value, key)) {
          ehrConstruction.value[key] = value
        }
      })
    }

    if (data.communal) {
      Object.entries(data.communal).forEach(([key, value]) => {
        if (!isEmpty(value) && isEmpty(ehrCommunal.value[key]) && Object.hasOwn(ehrCommunal.value, key)) {
          ehrCommunal.value[key] = value
        }
      })
    }

    ehrLivingRoom.value = data.livingRoom
    ehrNotLivingRoom.value = data.notLivingRoom
    ehrGeometry.value = data.geometry

    // Do not overwrite if user made changes
    if (mainForm.value['katastritunnus'] === '') {
      mainForm.value['katastritunnus'] = cadasterId.value
    }

    if (mainForm.value['aadress'] === '') {
      mainForm.value['aadress'] = ehrAds.value?.taisaadress
    }

    if (
      ehrLivingRoom.value.length === 1 &&
      ehrNotLivingRoom.value.length === 0
    ) {
      evaluationLivingRoom.value = ehrLivingRoom.value[0]
    }

    ehrLoaded.value = true
    ehrLoading.value = false
  }

  const setMapExport = data => {
    mapExport.value[data.key] = data.value
  }

  // Derived ESG capture readiness — the single truth the subheader progress
  // bar, the status pill and the export auto-wait all read. Enabled = the
  // DETAIL_ESG keys the user has toggled on; captured = those whose offscreen
  // map has produced a real data-URL ('' is EsgItemComponent's error sentinel,
  // 'data:,' an empty canvas).
  const enabledEsgMapKeys = computed(() =>
    Object.keys(DETAIL_ESG).filter(key => esgData.value[key]?.user?.status),
  )
  const capturedEsgMapKeys = computed(() =>
    enabledEsgMapKeys.value.filter(key => {
      const value = mapExport.value[key]
      return value && value !== 'data:,'
    }),
  )
  const esgMapsReady = computed(
    () =>
      enabledEsgMapKeys.value.length === 0 ||
      capturedEsgMapKeys.value.length === enabledEsgMapKeys.value.length,
  )

  // Every map capture the export waits for — the enabled ESG maps plus the
  // plannings map — as one counter for the Ekspordi button and the bar.
  const exportMapUnits = computed(() => enabledEsgMapKeys.value.length + 1)
  const exportMapUnitsReady = computed(
    () => capturedEsgMapKeys.value.length + (planningMapReady.value ? 1 : 0),
  )

  // Recapture signal: EsgItemComponents capture independently (no
  // orchestrator), so the export auto-wait nudges stragglers through the
  // store. Each item watches the nonce and re-runs its capture when listed.
  const esgRecaptureRequest = ref({ nonce: 0, keys: [] })
  const requestEsgRecapture = keys => {
    esgRecaptureRequest.value = {
      nonce: esgRecaptureRequest.value.nonce + 1,
      keys,
    }
  }

  // Export phase for the subheader progress bar + pill. Written by
  // useExportDetail, read by Detail.vue.
  // 'idle' | 'waiting-maps' | 'uploading' | 'processing' | 'done' | 'error'
  const exportPhase = ref('idle')
  const exportUploadProgress = ref(0)

  const updateMainForm = data => {
    mainForm.value = data
  }

  return {
    evaluationId,
    objectId,
    cadasterId,
    ehrId,
    // Raw EHR geometry must be store STATE (not just the ehrGeometry computed
    // wrapping it) so Detail.vue's $reset clears it between evaluations —
    // otherwise a land parcel visited after a building keeps the building's
    // shape on the makro/mikro maps.
    geometry,
    evaluationAuthor,
    evaluationApproverId,
    evaluationLivingRoom,
    evaluationNotLivingRoom,
    evaluationTemplateId,
    mainForm,
    ehrGeneral,
    ehrTechnical,
    ehrConstruction,
    ehrCommunal,
    ehrLivingRoom,
    ehrNotLivingRoom,
    ehrGeometry,
    ehrAds,
    ehrLoading,
    ehrLoaded,
    infra,
    realEstateGeneral,
    realEstatePart1Main,
    realEstatePart1Entries,
    realEstatePart2,
    realEstatePart3,
    realEstatePart4,
    esgData,
    esgSummary,
    buildings,
    mapExport,
    setMapExport,
    enabledEsgMapKeys,
    capturedEsgMapKeys,
    esgMapsReady,
    esgRecaptureRequest,
    requestEsgRecapture,
    exportMapUnits,
    exportMapUnitsReady,
    exportPhase,
    exportUploadProgress,
    setPropertyData,
    setEhrData,
    landBoard,
    documents,
    selectedDocuments,
    getSelectedDocuments,
    restrictions,
    planningInfo,
    planningInfoError,
    planningMapView,
    planningInfoPending,
    planningMapGeneration,
    planningCaptureGeneration,
    planningMapReady,
    planningRecaptureRequest,
    requestPlanningRecapture,
    fetchPlanningInfo,
    fetchOverlaySource,
    fetchPlanningLegend,
    selectedBuildings,
    updateMainForm,
    interior,
    isExporting,
    getInitialMainForm,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDetailStore, import.meta.hot))
}
